import moment from 'moment';
import 'moment-timezone';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import ReactJsonPretty from 'react-json-pretty';
import { injectIntl } from 'react-intl';

import {
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Row,
  Button,
  ModalFooter,
  Collapse,
} from 'reactstrap';
import '../../assets/chatlogs-slide-modal.css';
import { Colxx } from '../../components/common/CustomBootstrap';
import Pagination from './Pagination';
import { DataListIcon, ImageListIcon } from '../../components/svg';

const projectJsonTheme = {
  main: 'background: #f6f8fa; color: #2d3748;',
  key: 'color: #007bff; font-weight: 600;',
  string: 'color: #e83e8c;',
  value: 'color: #20c997;',
  null: 'color: #6c757d;',
  number: 'color: #17a2b8;',
  colon: 'color: #2d3748;',
  comma: 'color: #2d3748;',
  bracket: 'color: #2d3748;',
};

const reactJsonPrettyWrapStyle = `
.react-json-pretty, .react-json-pretty pre, .json-pretty-wrap, .json-pretty-wrap pre {
  white-space: pre-wrap !important;
  word-break: break-word !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
`;

function highlightContext(context, search) {
  if (!search) {
    return context.split(/\r?\n/).map((line, idx, arr) => (
      <span key={idx}>
        {line}
        {idx < arr.length - 1 && <br />}
      </span>
    ));
  }
  const esc = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(esc, 'gi');
  return context.split(/\r?\n/).map((line, idx, arr) => {
    const parts = line.split(regex);
    const matches = line.match(regex);
    if (!matches) {
      return (
        <span key={idx}>
          {line}
          {idx < arr.length - 1 && <br />}
        </span>
      );
    }
    let result = [];
    for (let i = 0; i < parts.length; i++) {
      result.push(<span key={i + '-part'}>{parts[i]}</span>);
      if (i < matches.length) {
        result.push(
          <span
            key={i + '-match'}
            style={{
              background: '#ffe066',
              color: '#222',
              borderRadius: '3px',
              padding: '0 2px',
            }}
          >
            {matches[i]}
          </span>
        );
      }
    }
    return (
      <span key={idx}>
        {result}
        {idx < arr.length - 1 && <br />}
      </span>
    );
  });
}

const ChatLogs = ({
  chatLogs,
  isOpen,
  toggle,
  currentPage,
  totalPages,
  onChangePage,
  conversationId,
  timeZone,
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTexts, setSearchTexts] = useState({});
  const [lastConversationId, setLastConversationId] = useState();
  const [showLoader, setShowLoader] = useState(true);
  // Track which panels have their Tools JSON expanded
  const [showToolsJson, setShowToolsJson] = useState({});

  // Only show logs if they match the current conversationId
  const logsAreFresh =
    Array.isArray(chatLogs) &&
    chatLogs.length > 0 &&
    lastConversationId !== null &&
    lastConversationId === conversationId;

  const modelName = logsAreFresh && chatLogs[0].model ? chatLogs[0].model : '';

  useEffect(() => {
    // When conversationId changes, show loader and clear open panels/search
    setShowLoader(true);
    setOpenIndex(null);
    setSearchTexts({});
  }, [conversationId]);

  useEffect(() => {
    // When new logs arrive for the current conversation, hide loader and set lastConversationId
    if (Array.isArray(chatLogs) && chatLogs.length > 0 && conversationId) {
      setLastConversationId(conversationId);
      setShowLoader(false);
    }
  }, [chatLogs, conversationId]);

  const togglePanel = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div>
      <style>{reactJsonPrettyWrapStyle}</style>
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.45)',
            zIndex: 9998,
            transition: 'opacity 0.3s',
          }}
        />
      )}
      <div
        className={'chatlogs-slide-modal' + (isOpen ? ' open' : '')}
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        <ModalHeader toggle={toggle}>Chat Logs</ModalHeader>
        <ModalBody>
          {showLoader || !logsAreFresh ? (
            <div className="loading" />
          ) : logsAreFresh ? (
            <div>
              <h5 className="mb-4">
                <strong>Model:</strong> {modelName}
              </h5>
              {chatLogs.map((entry, index) => (
                <Card
                  key={index}
                  className="mb-3 shadow-sm border-0"
                  style={{ borderRadius: '14px', overflow: 'hidden' }}
                >
                  <div
                    className={
                      'd-flex align-items-center justify-content-between px-4 py-3 ' +
                      (openIndex === index ? 'bg-light' : 'bg-white') +
                      ' text-dark'
                    }
                    style={{
                      cursor: 'pointer',
                      borderRadius:
                        openIndex === index ? '14px 14px 0 0' : '14px',
                      fontWeight: 600,
                      fontSize: '1rem',
                      transition: 'border-radius 0.2s',
                    }}
                    onClick={() => togglePanel(index)}
                  >
                    <span
                      style={{
                        fontFamily: 'Funnel Sans, Quicksand, Arial, sans-serif',
                        color: '#000',
                        fontWeight: 'normal',
                        flex: 1,
                        display: 'inline-block',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {entry.query}
                    </span>
                    {entry.createdOn && (
                      <span
                        style={{
                          fontSize: '0.75em',
                          color: '#888',
                          marginLeft: 16,
                          marginRight: 8,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {timeZone
                          ? moment
                              .utc(entry.createdOn)
                              .tz(timeZone)
                              .format('MMM Do, YYYY [at] h:mm a')
                          : moment(entry.createdOn).format(
                              'MMM Do, YYYY [at] h:mm a'
                            )}
                      </span>
                    )}

                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: '20px',
                        color: '#6c757d',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {openIndex === index ? '▾' : '▸'}
                    </span>
                  </div>
                  <Collapse isOpen={openIndex === index}>
                    <CardBody
                      className="bg-white text-dark px-3 py-2"
                      style={{
                        borderRadius: '0 0 14px 14px',
                        fontFamily: 'Funnel Sans, Quicksand, Arial, sans-serif',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                        position: 'relative',
                        minHeight: 64,
                      }}
                    >
                      {/* Show entry.instructions as plain text, toggleable, inside expanded panel */}
                      {typeof entry.instructions !== 'undefined' &&
                        entry.instructions &&
                        entry.instructions !== '' && (
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                setShowToolsJson((prev) => ({
                                  ...prev,
                                  ['instructions-' + index]:
                                    !prev['instructions-' + index],
                                }))
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginBottom: 10,
                                marginTop: 10,
                                padding: 0,
                                fontSize: '1em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {showToolsJson['instructions-' + index]
                                ? '▾'
                                : '▸'}{' '}
                              <i
                                className="iconsminds-information"
                                style={{
                                  fontSize: '1em',
                                  minWidth: 14,
                                  minHeight: 14,
                                  marginRight: 2,
                                  color: '#007bff',
                                }}
                              ></i>
                              <b>Conversation Flow Instructions</b>
                            </button>
                            {showToolsJson['instructions-' + index] && (
                              <div
                                style={{
                                  background: '#f6f8fa',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 8,
                                  padding: '10px 16px',
                                  fontSize: '0.95em',
                                  fontFamily:
                                    'Funnel Sans, Quicksand, Arial, monospace',
                                  wordBreak: 'break-all',
                                  maxHeight: 220,
                                  overflowY: 'auto',
                                }}
                              >
                                <pre
                                  style={{
                                    margin: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    color: '#222',
                                  }}
                                >
                                  {entry.instructions}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      {/* Show entry.tools as JSON if present, inside expanded panel, with toggle */}
                      {typeof entry.tools !== 'undefined' &&
                        entry.tools &&
                        entry.tools !== '' && (
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                setShowToolsJson((prev) => ({
                                  ...prev,
                                  [index]: !prev[index],
                                }))
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginBottom: 10,
                                marginTop: 10,
                                padding: 0,
                                fontSize: '1em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {showToolsJson[index] ? '▾' : '▸'}{' '}
                              <i
                                className="iconsminds-gear"
                                style={{
                                  fontSize: '1em',
                                  minWidth: 14,
                                  minHeight: 14,
                                  marginRight: 2,
                                  color: '#007bff',
                                }}
                              ></i>
                              <b>Conversation Flow API</b>
                            </button>
                            {showToolsJson[index] && (
                              <div
                                className="json-pretty-wrap"
                                style={{
                                  background: '#f6f8fa',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 8,
                                  padding: '10px 16px',
                                  fontSize: '0.95em',
                                  fontFamily:
                                    'Funnel Sans, Quicksand, Arial, monospace',
                                  maxHeight: 220,
                                  overflowY: 'auto',
                                  margin: 0,
                                  boxSizing: 'border-box',
                                }}
                              >
                                <ReactJsonPretty
                                  data={(() => {
                                    try {
                                      return typeof entry.tools === 'string'
                                        ? JSON.parse(entry.tools)
                                        : entry.tools;
                                    } catch {
                                      return typeof entry.tools === 'string'
                                        ? entry.tools
                                        : String(entry.tools);
                                    }
                                  })()}
                                  theme={projectJsonTheme}
                                  themeClassName="custom-json-pretty"
                                  style={{
                                    fontSize: '0.95em',
                                    borderRadius: '8px',
                                    background: '#f6f8fa',
                                    fontFamily:
                                      'Funnel Sans, Quicksand, Arial, monospace',
                                    margin: 0,
                                    maxWidth: '100%',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                      {/* Expand/collapse for Knowledge Hub, History, Response */}

                      {/* Knowledge Hub Extracted Content */}
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            setShowToolsJson((prev) => {
                              const key = 'context-' + index;
                              const isOpen =
                                Object.prototype.hasOwnProperty.call(prev, key)
                                  ? prev[key]
                                  : true;
                              return {
                                ...prev,
                                [key]: !isOpen,
                              };
                            })
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginBottom: 10,
                            marginTop: 10,
                            padding: 0,
                            fontSize: '1em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          {showToolsJson['context-' + index] === false
                            ? '▸'
                            : '▾'}{' '}
                          <i
                            className="iconsminds-file"
                            style={{
                              fontSize: '1em',
                              minWidth: 14,
                              minHeight: 14,
                              marginRight: 2,
                              color: '#007bff',
                            }}
                          ></i>
                          <b>Knowledge Hub Extracted Content</b>
                        </button>
                      </div>
                      {showToolsJson['context-' + index] !== false && (
                        <div className="wrap-text">
                          <div style={{ marginTop: '12px', marginBottom: 8 }}>
                            <input
                              type="text"
                              placeholder="Search context..."
                              value={searchTexts[index] || ''}
                              onChange={(e) => {
                                setSearchTexts((st) => ({
                                  ...st,
                                  [index]: e.target.value,
                                }));
                              }}
                              style={{
                                width: '100%',
                                fontSize: '0.85em',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                outline: 'none',
                                marginBottom: 4,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              background: '#f6f8fa',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              padding: '10px 16px',
                              fontSize: '0.95em',
                              fontFamily:
                                'Funnel Sans, Quicksand, Arial, monospace',
                              maxHeight: '30vh',
                              overflowY: 'auto',
                              margin: 0,
                              boxSizing: 'border-box',
                            }}
                          >
                            {typeof entry.context === 'string'
                              ? highlightContext(
                                  entry.context,
                                  searchTexts[index] || ''
                                )
                              : entry.context}
                          </div>
                        </div>
                      )}

                      {/* History Section */}
                      {(() => {
                        let json;
                        try {
                          json =
                            typeof entry.history === 'string'
                              ? JSON.parse(entry.history)
                              : entry.history;
                        } catch (e) {
                          json = entry.history;
                        }
                        if (
                          json === undefined ||
                          json === null ||
                          (typeof json === 'string' && json.trim() === '') ||
                          (Array.isArray(json) && json.length === 0)
                        ) {
                          return null;
                        }
                        return (
                          <>
                            <div className="wrap-text">
                              <button
                                type="button"
                                onClick={() =>
                                  setShowToolsJson((prev) => {
                                    const key = 'history-' + index;
                                    const isOpen =
                                      Object.prototype.hasOwnProperty.call(
                                        prev,
                                        key
                                      )
                                        ? prev[key]
                                        : true;
                                    return {
                                      ...prev,
                                      [key]: !isOpen,
                                    };
                                  })
                                }
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  marginBottom: 10,
                                  marginTop: 10,
                                  padding: 0,
                                  fontSize: '1em',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                {showToolsJson['history-' + index] === false
                                  ? '▸'
                                  : '▾'}{' '}
                                <i
                                  className="iconsminds-clock"
                                  style={{
                                    fontSize: '1em',
                                    minWidth: 14,
                                    minHeight: 14,
                                    marginRight: 2,
                                    color: '#007bff',
                                  }}
                                ></i>
                                <b>History</b>
                              </button>
                            </div>
                            {showToolsJson['history-' + index] !== false && (
                              <div className="wrap-text">
                                <div
                                  style={{
                                    marginTop: '12px',
                                    position: 'static',
                                  }}
                                />
                                <div
                                  className="json-pretty-wrap"
                                  style={{ position: 'static' }}
                                >
                                  <ReactJsonPretty
                                    data={json}
                                    theme={projectJsonTheme}
                                    themeClassName="custom-json-pretty"
                                    style={{
                                      fontSize: '0.95em',
                                      borderRadius: '8px',
                                      padding: '12px 16px',
                                      background: '#f6f8fa',
                                      fontFamily:
                                        'Funnel Sans, Quicksand, Arial, monospace',
                                      margin: 0,
                                      maxWidth: '100%',
                                      position: 'static',
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Response Section */}
                      <div className="wrap-text">
                        <button
                          type="button"
                          onClick={() =>
                            setShowToolsJson((prev) => {
                              const key = 'response-' + index;
                              const isOpen =
                                Object.prototype.hasOwnProperty.call(prev, key)
                                  ? prev[key]
                                  : true;
                              return {
                                ...prev,
                                [key]: !isOpen,
                              };
                            })
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginBottom: 10,
                            marginTop: 10,
                            padding: 0,
                            fontSize: '1em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          {showToolsJson['response-' + index] === false
                            ? '▸'
                            : '▾'}{' '}
                          <i
                            className="iconsminds-speach-bubble"
                            style={{
                              fontSize: '1em',
                              minWidth: 14,
                              minHeight: 14,
                              marginRight: 2,
                              color: '#007bff',
                            }}
                          ></i>
                          <b>Response</b>
                        </button>
                      </div>
                      {showToolsJson['response-' + index] !== false && (
                        <div className="wrap-text">
                          <div style={{ marginTop: '12px' }} />
                          {(() => {
                            let json;
                            try {
                              json =
                                typeof entry.response === 'string'
                                  ? JSON.parse(entry.response)
                                  : entry.response;
                            } catch (e) {
                              json = entry.response;
                            }
                            if (
                              json &&
                              typeof json === 'object' &&
                              json.arguments
                            ) {
                              return (
                                <div className="json-pretty-wrap">
                                  <ReactJsonPretty
                                    data={json.arguments}
                                    theme={projectJsonTheme}
                                    themeClassName="custom-json-pretty"
                                    style={{
                                      fontSize: '0.95em',
                                      borderRadius: '8px',
                                      padding: '12px 16px',
                                      background: '#f6f8fa',
                                      fontFamily:
                                        'Funnel Sans, Quicksand, Arial, monospace',
                                      margin: 0,
                                      maxWidth: '100%',
                                    }}
                                  />
                                </div>
                              );
                            } else {
                              return <span className="text-muted"></span>;
                            }
                          })()}
                        </div>
                      )}

                      {/* ...existing code... */}
                      {/* Annotation display below timing/tokens row, in normal flow, no absolute positioning */}
                      {Array.isArray(entry.annotation) &&
                        entry.annotation.length > 0 &&
                        entry.annotation.some(
                          (a) => a && a.sourceFile && a.pageNumber != null
                        ) && (
                          <div
                            style={{
                              fontSize: '0.85em',
                              color: '#555',
                              background: '#f6f8fa',
                              border: '1px solid #bbb',
                              borderRadius: 6,
                              padding: '2px 8px',
                              minWidth: 0,
                              textAlign: 'left',
                              display: 'block',
                              marginTop: 8,
                              maxWidth: '60%',
                              wordBreak: 'break-word',
                            }}
                            title="Source file and page references"
                          >
                            {entry.annotation
                              .filter(
                                (a) => a && a.sourceFile && a.pageNumber != null
                              )
                              .map((a, i) => (
                                <span key={i} style={{ display: 'block' }}>
                                  📄 {a.sourceFile}{' '}
                                  {a.pageNumber !== 0
                                    ? ` p.${a.pageNumber}`
                                    : ''}
                                </span>
                              ))}
                          </div>
                        )}

                      {/* Timing and tokens info at bottom border */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 12,
                          borderTop: '1px solid #eee',
                          padding: '8px 0 0 0',
                          marginTop: 12,
                          minHeight: 40,
                        }}
                      >
                        {/* ...existing code... */}
                        {typeof entry.toolName !== 'undefined' &&
                          entry.toolName &&
                          entry.toolName.trim() !== '' && (
                            <div
                              style={{
                                border: '2px solid #888',
                                borderRadius: 6,
                                padding: '2px 10px',
                                fontSize: '0.95em',
                                fontWeight: 600,
                                color: '#444',
                                background: '#f8f9fa',
                                display: 'inline-flex',
                                alignItems: 'center',
                                minWidth: 48,
                                textAlign: 'center',
                                gap: 6,
                              }}
                              title={'Conversation Flow API Triggered'}
                            >
                              <span title="Tool" role="img" aria-label="Tool">
                                🛠️
                              </span>{' '}
                              {entry.toolName}
                            </div>
                          )}

                        {typeof entry.tokens !== 'undefined' &&
                          entry.tokens > 0 && (
                            <div
                              style={{
                                border: '2px solid #888',
                                borderRadius: 6,
                                padding: '2px 10px',
                                fontSize: '0.95em',
                                fontWeight: 600,
                                color: '#444',
                                background: '#f8f9fa',
                                display: 'inline-flex',
                                alignItems: 'center',
                                minWidth: 48,
                                textAlign: 'center',
                                gap: 6,
                              }}
                              title={'Tokens consumed'}
                            >
                              <span
                                title="Tokens"
                                role="img"
                                aria-label="Tokens"
                              >
                                🔢
                              </span>{' '}
                              {entry.tokens}
                            </div>
                          )}

                        {typeof entry.searchDocumentTime !== 'undefined' &&
                          entry.searchDocumentTime > 0 && (
                            <div
                              style={{
                                border: '2px solid #888',
                                borderRadius: 6,
                                padding: '2px 10px',
                                fontSize: '0.95em',
                                fontWeight: 600,
                                color: '#444',
                                background: '#f8f9fa',
                                display: 'inline-flex',
                                alignItems: 'center',
                                minWidth: 48,
                                textAlign: 'center',
                                gap: 6,
                              }}
                              title={'Time to search Knowledge Hub'}
                            >
                              <span
                                title="Search Time"
                                role="img"
                                aria-label="Search"
                              >
                                🔍
                              </span>{' '}
                              {entry.searchDocumentTime > 999
                                ? (entry.searchDocumentTime / 1000).toFixed(1) +
                                  ' s'
                                : entry.searchDocumentTime + ' ms'}
                            </div>
                          )}
                        {typeof entry.llmResponseTime !== 'undefined' &&
                          entry.llmResponseTime > 0 && (
                            <div
                              style={{
                                border: '2px solid #888',
                                borderRadius: 6,
                                padding: '2px 10px',
                                fontSize: '0.95em',
                                fontWeight: 600,
                                color: '#444',
                                background: '#f8f9fa',
                                display: 'inline-flex',
                                alignItems: 'center',
                                minWidth: 48,
                                textAlign: 'center',
                                gap: 6,
                              }}
                              title={'Time to generate response'}
                            >
                              <span
                                title="LLM Time"
                                role="img"
                                aria-label="LLM"
                              >
                                🤖
                              </span>{' '}
                              {entry.llmResponseTime > 999
                                ? (entry.llmResponseTime / 1000).toFixed(1) +
                                  ' s'
                                : entry.llmResponseTime + ' ms'}
                            </div>
                          )}
                      </div>
                    </CardBody>
                  </Collapse>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center">No logs available</div>
          )}
        </ModalBody>
        <ModalFooter>
          {logsAreFresh && (
            <Pagination
              currentPage={currentPage}
              totalPage={totalPages}
              onChangePage={(i) => onChangePage(i)}
            />
          )}
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </div>
    </div>
  );
};

const mapStateToProps = ({ settings }) => {
  const { timeZone } = settings;
  return { timeZone };
};

export default injectIntl(connect(mapStateToProps, {})(ChatLogs));
